/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useDispatch, useSelector } from 'react-redux';
import {
  selectTrendSymmetricComparisonToggle,
  selectTrendTimeDateComparison,
  selectTrendTimeAlignmentToggle,
  selectTrendSegmentChartBreakdown,
  selectIsSegmentDetailed,
  selectTrendSegmentFocusOn,
  selectIsForecast,
  selectTrendStartDate,
  selectTrendEndDate,
  selectIsCumulative,
  selectMovingAverage,
  selectTrendTimeWeeklyView,
  selectIsTrendDeAggregate,
  selectIsMovingAverage,
  selectIsPercentageChange,
  selectTrendTimeStartDate,
  selectTrendTimeEndDate,
  selectTrendGranularity,
  selectTrendData,
  selectTrendDataResponseId,
  selectSegmentGroup1,
  selectSegmentGroup2,
  selectSegmentSetAName,
  selectSegmentSetBName,
  selectTrendHotelNormalizedView,
  selectTrendHotelFocusOn,
  selectIsPrimary,
} from 'modules/dashboard/components/tab-container/trend-tabs/selectors';
import {
  selectHotelName,
  selectLastPartialBookingDate,
  selectLocalCurrency,
  selectPartialHours,
  selectPmsSync,
  selectResetBookingDate,
  selectTrendBookingDate,
  selectTrendTargetDate,
  selectVisualFilters,
} from 'modules/dashboard/selectors';
import { useEffect, useMemo, useRef, useState } from 'react';
import MuiTabular from 'modules/common/components/mui-tabular';
import { Box, Card, Grid, IconButton, Typography } from '@mui/material';
import useAbortableRequest from 'modules/common/hooks/use-abortable-request';
import { isRequestCanceled } from 'modules/common/utils/request';
import { API, EXPORT_OPTIONS } from 'modules/dashboard/constants';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import CollapseIcon from 'modules/common/components/collapse-icon';
import { ENVIRONMENT } from 'config';
import {
  selectAuthUser,
  selectOrganizationId,
  selectSessionId,
} from 'modules/common/auth/selectors';
import { AssistantIcon } from 'modules/common/components';
import { EXPORT_BUTTON_STYLE } from 'modules/common/components/mui-tabular/style';
import DownloadIcon from '@mui/icons-material/Download';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { useDebouncedCallback } from 'use-debounce';
import { BREAKDOWN_LIST_GETTER_COLUMNS } from '../../../../constants';
import {
  buildSegmentTimeSchema,
  buildTrendFilters,
  buildCombinedVisualHeaderData,
  getBreakdownName,
  getSegmentColumns,
  groupDataWithParent,
  handleCeruleanLogging,
  getTimeSegmentExportFileName,
} from '../../../../functions';
import TabularFilterHeader from '../../filter-header';
import { trendActions } from '../../../../slice';

/**
 * Trend Segment tabular visual report with filters
 */

const MuiTimeSegmentTabularWidget = ({ setIsTimeLoaded, isLoaded }) => {
  const bookingDate = useSelector(selectTrendBookingDate);
  const currencyType = useSelector(selectLocalCurrency);
  const hotels = useSelector(selectHotelName);
  const targetStartDate = useSelector(selectTrendStartDate);
  const targetEndDate = useSelector(selectTrendEndDate);
  const alignmentToggle = useSelector(selectTrendTimeAlignmentToggle);
  const symmetricComparisonToggle = useSelector(selectTrendSymmetricComparisonToggle);
  const dateComparison = useSelector(selectTrendTimeDateComparison);
  const pms = useSelector(selectPmsSync);
  const visualFilters = useSelector(selectVisualFilters);
  const chartBreakdown = useSelector(selectTrendSegmentChartBreakdown);
  const isDetailed = useSelector(selectIsSegmentDetailed);
  const focusOn = useSelector(selectTrendSegmentFocusOn);
  const isForecast = useSelector(selectIsForecast);
  const isCumulative = useSelector(selectIsCumulative);
  const organizationId = useSelector(selectOrganizationId);
  const movingAverage = useSelector(selectMovingAverage);
  const isMovingAverage = useSelector(selectIsMovingAverage);
  const isWeeklyView = useSelector(selectTrendTimeWeeklyView);
  const isPercentageChange = useSelector(selectIsPercentageChange);
  const isBreakdown = useSelector(selectIsPrimary);
  const isTrendDeAggregate = useSelector(selectIsTrendDeAggregate);
  const endDate = useSelector(selectTrendTimeEndDate);
  const startDate = useSelector(selectTrendTimeStartDate);
  const segmentGroupA = useSelector(selectSegmentGroup1);
  const segmentGroupB = useSelector(selectSegmentGroup2);
  const segmentGroupAName = useSelector(selectSegmentSetAName);
  const segmentGroupBName = useSelector(selectSegmentSetBName);
  const resetBookingDate = useSelector(selectResetBookingDate);
  const user = useSelector(selectAuthUser);
  const sessionId = useSelector(selectSessionId);
  const trendData = useSelector(selectTrendData);
  const trendDataResponseId = useSelector(selectTrendDataResponseId);
  const trendGranularity = useSelector(selectTrendGranularity);
  const hotelFocusOn = useSelector(selectTrendHotelFocusOn);
  const partialHours = useSelector(selectPartialHours);
  const lastBookingDate = useSelector(selectLastPartialBookingDate);
  const normalization = useSelector(selectTrendHotelNormalizedView);
  const targetDate = useSelector(selectTrendTargetDate);
  const dispatch = useDispatch();
  const abortableRequest = useAbortableRequest();
  //
  const tableRef = useRef();
  //
  const [exportType, setExportType] = useState(EXPORT_OPTIONS.DATA_EXPORT);
  //
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDisplay, setIsDisplay] = useState(true);
  //
  const [exportData, setExportData] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  // Debounce the request to avoid excessive calls
  const fetchData = useDebouncedCallback(async () => {
    // Build the filters for the API request
    const filters = buildTrendFilters({
      bookingDate,
      targetStartDate,
      targetEndDate,
      alignmentToggle,
      symmetricComparisonToggle,
      dateComparison,
      currencyType,
      hotels,
      pms,
      chartBreakdown,
      visualFilters,
      isDetailed,
      focusOn,
      isForecast,
      movingAverage,
      isMovingAverage,
      startDate,
      endDate,
      segmentGroupA,
      segmentGroupB,
      segmentGroupAName,
      segmentGroupBName,
      resetBookingDate,
      trendGranularity,
      hotelFocusOn,
      partialHours,
      lastBookingDate,
      normalization,
      originalTargetDate: targetDate,
      isWeeklyAdjusted: isWeeklyView && !isCumulative,
    });
    //
    const columns = getSegmentColumns({
      isPercentageChange,
      isMovingAverage,
      isCumulative,
      isWeeklyView,
      isTrendDeAggregate,
      segmentGroupA,
      segmentGroupB,
      isHotelComparison: hotelFocusOn?.id !== hotels?.id,
    });
    //
    let start;
    try {
      setIsTimeLoaded(false);
      // Log the request time for performance monitoring
      handleCeruleanLogging({
        message: `Trend Time Tabular Filters: ${JSON.stringify(filters)}`,
        sessionId,
        user,
        eventType: 'Cerulean Filters',
      });
      handleCeruleanLogging({
        message: `Trend Tabular Request start: ${new Date()}`,
        sessionId,
        user,
        eventType: 'Cerulean Loading',
      });
      start = performance.now();
      // fetch breakdown list
      const breakdownListData = await abortableRequest(API.GET_JS_VISUAL_DATA, {
        organizationId,
        columns: BREAKDOWN_LIST_GETTER_COLUMNS,
        filters,
      });
      //
      const breakdown = getBreakdownName(isDetailed, visualFilters, chartBreakdown);
      const breakdownList = breakdownListData?.map((item) => item?.[breakdown]);
      if (filters?.breakDownList?.conditions) {
        const isTotalInclude = filters.breakDownList.conditions.values
          .map((v) => v.replace(/\u200B/g, '').trim())
          .includes('Total');
        filters.breakDownList.conditions.values = [
          ...breakdownList,
          ...(isTotalInclude ? ['Total'] : []),
        ];
      }
      //
      setExportData({
        columns,
        filters,
        title: 'mui-tabular-trend-segment',
      });
      //
      dispatch(
        trendActions.getTrendData({
          organizationId,
          columns,
          filters,
        })
      );
    } catch (error) {
      if (isRequestCanceled(error)) return;
      handleCeruleanLogging({
        message: `Error fetching trend time tabular data: ${error}`,
        sessionId,
        user,
        eventType: 'Cerulean Error',
      });
    } finally {
      // Log the time taken for the request
      const end = performance.now();
      handleCeruleanLogging({
        message: `Trend Tabular Request finished: ${new Date()}`,
        sessionId,
        user,
        eventType: 'Cerulean Loaded',
      });
      handleCeruleanLogging({
        message: `Trend Tabular Request time: ${((end - start) / 1000).toFixed(2)} seconds`,
        sessionId,
        user,
        eventType: 'Cerulean Request Time',
      });
    }
  }, 500);
  //
  useEffect(() => {
    // loaded when trend data is available or a request is completed, also after an error
    if (trendData || trendDataResponseId > 0) {
      setIsTimeLoaded(true);
    }
  }, [trendData, trendDataResponseId]);
  //
  useEffect(() => {
    fetchData();
  }, [
    bookingDate,
    currencyType,
    hotels,
    alignmentToggle,
    symmetricComparisonToggle,
    dateComparison,
    setIsTimeLoaded,
    targetStartDate,
    targetEndDate,
    pms,
    focusOn,
    isCumulative,
    isForecast,
    movingAverage,
    isWeeklyView,
    isMovingAverage,
    isPercentageChange,
    startDate,
    endDate,
    isBreakdown,
    segmentGroupA,
    segmentGroupB,
    segmentGroupAName,
    segmentGroupBName,
    trendGranularity,
    hotelFocusOn,
    partialHours,
    lastBookingDate,
    normalization,
    targetDate,
    isDisplay
  ]);
  //
  const headerData = useMemo(
    () =>
      buildCombinedVisualHeaderData({
        targetStartDate,
        targetEndDate,
        hotelName: hotels?.label,
        startDate,
        endDate,
        bookingDate,
        currencyType,
        pmsSync: pms,
        isCumulative,
        isPercentageChange,
        dateComparison,
        isWeeklyView,
        isMovingAverage,
        resetBookingDate,
        symmetricComparisonToggle,
        isHotelComparison: hotelFocusOn?.id !== hotels?.id,
        hotelFocusOn: hotelFocusOn?.label,
        isComparisonHotelSet: Boolean(hotelFocusOn?.hotels),
        partialHours,
        lastBookingDate,
        alignmentToggle,
        originalTargetDate: targetDate,
      }),
    [
      hotels,
      bookingDate,
      targetEndDate,
      targetStartDate,
      alignmentToggle,
      startDate,
      endDate,
      symmetricComparisonToggle,
      currencyType,
      pms,
      dateComparison,
      isBreakdown,
      isCumulative,
      isPercentageChange,
      isWeeklyView,
      isMovingAverage,
      resetBookingDate,
      isTrendDeAggregate,
      trendGranularity,
      hotelFocusOn?.id,
      partialHours,
      lastBookingDate,
      targetDate,
    ]
  );
  //
  const fileName = useMemo(
    () =>
      getTimeSegmentExportFileName({
        isCumulative,
        isMovingAverage,
        isPercentageChange,
        isWeeklyView,
      }),
    [isCumulative, isMovingAverage, isPercentageChange, isWeeklyView]
  );
  //
  const onClick = () => {
    setIsDisplay(!isDisplay);
  };
  //
  const handleExpandCollapse = () => {
    if (isExpanded) {
      tableRef?.current?.handleCollapseAll();
    } else {
      tableRef?.current?.handleExpandAll();
    }
    setIsExpanded(!isExpanded);
  };
  //
  const breakdownName = getBreakdownName(isDetailed, visualFilters, chartBreakdown);
  const data = groupDataWithParent(trendData, breakdownName, isWeeklyView);
  const tableSchema = buildSegmentTimeSchema(
    trendGranularity,
    data?.[0],
    isWeeklyView,
    isCumulative
  );
  //
  return (
    <Grid
      container
      direction="row"
      justifyContent="center"
      alignItems="flex-start"
      className={styles.mainGrid}
      mb={10}
    >
      <Card
        sx={{
          width: '100%',
          margin: '1rem 8rem',
        }}
      >
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          height={40}
          width="100%"
        >
          <Grid item pl={2}>
            {headerData?.title}
          </Grid>
          <Grid item>
            <Grid
              item
              container
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              width="100%"
            >
              {ENVIRONMENT.IS_VISUAL_INTERPRETER_ENABLED?.toLowerCase() === 'true' && (
                <Grid item mt={1} mr={4}>
                  <AssistantIcon
                    exportData={exportData}
                    visualName={headerData?.title}
                    isProcessing={isExporting}
                    setIsProcessing={setIsExporting}
                    disabled={isProcessing || !isLoaded || isExporting || !isDisplay}
                    isCerulean
                    getExcelExportData={tableRef?.current?.getExcelData}
                    exportType={exportType}
                    setExportType={setExportType}
                  />
                </Grid>
              )}

              <IconButton
                size="medium"
                onClick={() => setIsProcessing(true)}
                disabled={isProcessing || !isLoaded || isExporting || !isDisplay}
                sx={EXPORT_BUTTON_STYLE}
                id="trend-time-js-export-button"
              >
                <Typography
                  sx={{
                    mr: 1,
                  }}
                >
                  Export Data
                </Typography>
                <DownloadIcon />
              </IconButton>

              <CollapseIcon isDisplay={isDisplay} onClick={onClick} />
            </Grid>
          </Grid>
        </Grid>
        {isDisplay &&
          (!trendData ? (
            <Typography fontWeight={500} pb={4} textAlign="center">
              Loading Data...
            </Typography>
          ) : (
            <MuiTabular
              ref={tableRef}
              columns={tableSchema}
              isProcessing={isProcessing}
              isLoadingData={!isLoaded}
              setIsProcessing={setIsProcessing}
              isAggregate={!(isMovingAverage || isPercentageChange)}
              headerComponent={
                <Box width="100%" display="flex" justifyContent="space-between" alignItems="end">
                  <TabularFilterHeader
                    title=""
                    hotelName={headerData?.hotelName}
                    isHotelSet={Boolean(hotels?.hotels)}
                    sections={headerData?.sections}
                    comparisonHotelName={headerData?.comparisonHotelName}
                    isComparisonHotelSet={headerData?.isComparisonHotelSet}
                    chunkSize={isTrendDeAggregate && hotelFocusOn?.id === hotels?.id ? 2 : 1}
                  />
                  <IconButton
                    size="medium"
                    sx={{ mr: 4, mb: 1 }}
                    disabled={isProcessing || !isLoaded || !isDisplay}
                    title={
                      isExpanded
                        ? 'Collapse all expanded rows in the hierarchy'
                        : 'Expand all down one level in the hierarchy'
                    }
                    onClick={handleExpandCollapse}
                  >
                    {isExpanded ? (
                      <UnfoldLessIcon sx={{ fontSize: 20 }} />
                    ) : (
                      <UnfoldMoreIcon sx={{ fontSize: 20 }} />
                    )}
                  </IconButton>
                </Box>
              }
              data={data}
              headerFiltersInfo={headerData}
              customBackgroundStyle={{ paddingLeft: 3, maxWidth: { md: '72vw', lg: '76vw' } }}
              customHeaderStyle={{ lineHeight: 1.2 }}
              fileName={fileName}
              primaryTextColor={isPercentageChange ? '#000' : undefined}
              secondaryTextColor="#000"
              removeEmptyColumns
            />
          ))}
      </Card>
    </Grid>
  );
};
//
export default MuiTimeSegmentTabularWidget;
